// src/components/payment-links/forms/CreatePaymentLink.tsx
import { useState } from "react";
import {
  FiDollarSign,
  FiUser,
  FiMail,
  FiPhone,
  FiFileText,
  FiHash,
  FiClock,
} from "react-icons/fi";

import Field from "@/components/shared/ui/Field";
import Input from "@/components/shared/ui/Input";
import type { PaymentLinkDTO } from "@/api/features/payment-links";

const CURRENCIES = ["INR"];

type Props = {
  submitting?: boolean;
  /** Human-readable summary of a server error (shown as a banner in the modal). */
  serverError?: string | null;
  /** Per-field server validation messages, keyed by payload field name. */
  fieldErrors?: Record<string, string> | null;
  onSubmit: (payload: PaymentLinkDTO.Create) => void;
  onCancel: () => void;
};

function FieldError({ msg }: { msg?: string | null }) {
  if (!msg) return null;
  return <p className="mt-1 text-xs text-rose-600">{msg}</p>;
}

export default function CreatePaymentLink({
  submitting,
  serverError,
  fieldErrors,
  onSubmit,
  onCancel,
}: Props) {
  const fe = fieldErrors ?? {};
  const [amount, setAmount] = useState("");
  const [currencyCode, setCurrencyCode] = useState("INR");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [description, setDescription] = useState("");
  const [reference, setReference] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError("Enter a valid amount greater than 0.");
      return;
    }
    if (!customerName.trim()) {
      setError("Customer name is required.");
      return;
    }
    if (!description.trim()) {
      setError("A payment purpose / description is required.");
      return;
    }
    if (sendEmail && !customerEmail.trim()) {
      setError("A customer email is required to send the link by email.");
      return;
    }

    onSubmit({
      amount: Number(amt.toFixed(2)),
      currencyCode,
      customerName: customerName.trim(),
      customerEmail: customerEmail.trim() || null,
      customerPhone: customerPhone.trim() || null,
      description: description.trim(),
      reference: reference.trim() || null,
      // datetime-local has no timezone — send as ISO with local offset applied.
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      sendEmail,
    });
  }

  const banner = error || serverError;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {banner ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {banner}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Amount *" icon={<FiDollarSign />}>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
          />
          <FieldError msg={fe.amount} />
        </Field>

        <Field label="Currency *">
          <select
            value={currencyCode}
            onChange={(e) => setCurrencyCode(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Customer name *" icon={<FiUser />}>
        <Input
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          placeholder="Full name"
        />
        <FieldError msg={fe.customerName} />
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Customer email" icon={<FiMail />}>
          <Input
            type="email"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            placeholder="name@example.com"
          />
          <FieldError msg={fe.customerEmail} />
        </Field>

        <Field label="Customer phone" icon={<FiPhone />}>
          <Input
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            placeholder="10-digit number"
          />
          <FieldError msg={fe.customerPhone} />
        </Field>
      </div>

      <Field label="Purpose / description *" icon={<FiFileText />}>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. Dubai tour balance payment"
        />
        <FieldError msg={fe.description} />
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Internal reference" icon={<FiHash />}>
          <Input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Invoice no. / note (optional)"
          />
          <FieldError msg={fe.reference} />
        </Field>

        <Field label="Expires at" icon={<FiClock />}>
          <Input
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </Field>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={sendEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
        />
        Email the payment link to the customer immediately
      </label>

      <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
        >
          {submitting ? "Creating…" : "Create payment link"}
        </button>
      </div>
    </form>
  );
}
